import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { siteNav } from "@/mocks/site";

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState("");

  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome || menuOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const q = query.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setQuery("");
    setMenuOpen(false);
  };

  const isActive = (path: string) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  const linkBase = "relative px-1 py-2 text-sm font-label font-medium transition-colors";
  const linkColor = solid
    ? "text-foreground-700 hover:text-primary-600"
    : "text-background-50/85 hover:text-background-50";
  const activeColor = solid ? "text-primary-600" : "text-background-50";

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        solid
          ? "border-b border-background-200 bg-background-50/95 backdrop-blur"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 w-full items-center justify-between gap-4 px-4 md:h-[72px] md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-3">
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-md text-base font-bold ${
              solid ? "bg-primary-500 text-background-50" : "bg-background-50 text-primary-700"
            }`}
          >
            明
          </span>
          <span className="leading-tight">
            <span
              className={`block font-heading text-base font-bold tracking-wide md:text-lg ${
                solid ? "text-foreground-950" : "text-background-50"
              }`}
            >
              佐佐木明希
            </span>
            <span
              className={`hidden text-[10px] font-label uppercase tracking-[0.25em] md:block ${
                solid ? "text-foreground-400" : "text-background-50/70"
              }`}
            >
              Sasaki Aki Official
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-7 md:flex">
          {siteNav.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`${linkBase} ${isActive(item.path) ? activeColor : linkColor}`}
            >
              {item.label}
              {isActive(item.path) && (
                <span className="absolute inset-x-1 -bottom-0.5 h-0.5 rounded-full bg-primary-500" />
              )}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <form onSubmit={submitSearch} className="hidden items-center md:flex">
            <div
              className={`flex items-center gap-2 rounded-full border px-3 py-1.5 transition-colors ${
                solid
                  ? "border-background-300 bg-background-100 focus-within:border-primary-400"
                  : "border-background-50/40 bg-background-50/15 focus-within:border-background-50"
              }`}
            >
              <i
                className={`ri-search-line text-sm ${solid ? "text-foreground-500" : "text-background-50/80"}`}
                aria-hidden="true"
              />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜索资讯 / 作品"
                aria-label="站内搜索"
                className={`w-28 text-sm outline-none lg:w-40 ${
                  solid
                    ? "bg-transparent text-foreground-900 placeholder:text-foreground-400"
                    : "bg-transparent text-background-50 placeholder:text-background-50/70"
                }`}
              />
            </div>
          </form>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="打开菜单"
            aria-expanded={menuOpen}
            className={`flex h-9 w-9 items-center justify-center rounded-md text-xl transition-colors md:hidden ${
              solid ? "text-foreground-800 hover:bg-background-200" : "text-background-50 hover:bg-background-50/15"
            }`}
          >
            <i className={menuOpen ? "ri-close-line" : "ri-menu-line"} aria-hidden="true" />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 md:hidden">
          <div className="px-4 py-4">
            <form onSubmit={submitSearch} className="mb-4">
              <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2.5 focus-within:border-primary-400">
                <i className="ri-search-line text-base text-foreground-500" aria-hidden="true" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="搜索资讯 / 作品"
                  aria-label="站内搜索"
                  className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                />
              </div>
            </form>
            <nav className="flex flex-col">
              {siteNav.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center justify-between border-b border-background-100 py-3.5 text-base font-label ${
                    isActive(item.path) ? "text-primary-600" : "text-foreground-800"
                  }`}
                >
                  {item.label}
                  <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true" />
                </Link>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}